import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DateNavigation = ({ selectedDate, onDateChange, streak, className = "" }) => {
  const [showCalendar, setShowCalendar] = useState(false);

  // Ensure selectedDate is always a Date object
  const getDateObject = (date) => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    return new Date(date);
  };

  const formatDate = (date) => {
    const dateObj = getDateObject(date);
    return dateObj?.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const navigateDate = (direction) => {
    const dateObj = getDateObject(selectedDate);
    const newDate = new Date(dateObj);
    newDate?.setDate(newDate?.getDate() + direction);
    onDateChange(newDate);
  };

  const isToday = (date) => {
    const dateObj = getDateObject(date);
    const today = new Date();
    return dateObj?.toDateString() === today?.toDateString();
  };

  const isFuture = (date) => {
    const dateObj = getDateObject(date);
    const today = new Date();
    return dateObj > today;
  };

  return (
    <div className={`bg-card rounded-lg p-6 border border-border ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateDate(-1)}
            className="h-10 w-10"
          >
            <Icon name="ChevronLeft" size={16} />
          </Button>
          
          <div className="text-center">
            <h2 className="text-xl font-semibold text-text-primary">
              {formatDate(selectedDate)}
            </h2>
            <div className="flex items-center justify-center space-x-2 mt-1">
              {isToday(selectedDate) && (
                <span className="px-2 py-1 bg-accent text-white text-xs rounded-full">
                  Today
                </span>
              )}
              {isFuture(selectedDate) && (
                <span className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-full">
                  Future
                </span>
              )}
            </div>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateDate(1)}
            disabled={isFuture(selectedDate)}
            className="h-10 w-10"
          >
            <Icon name="ChevronRight" size={16} />
          </Button>
        </div>

        <div className="flex items-center space-x-4">
          {/* Wellness Streak */}
          <div className="flex items-center space-x-2 px-3 py-2 bg-success/10 rounded-lg border border-success/20">
            <Icon name="Flame" size={16} color="var(--color-success)" strokeWidth={2} />
            <div className="text-center">
              <div className="text-lg font-bold text-success">{streak}</div>
              <div className="text-xs text-success/80">day streak</div>
            </div>
          </div>

          {/* Calendar Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowCalendar(!showCalendar)}
            iconName="Calendar"
            iconPosition="left"
          >
            Calendar
          </Button>
        </div>
      </div>
      {/* Calendar Picker */}
      {showCalendar && (
        <div className="mt-4 p-4 bg-muted rounded-lg">
          <input
            type="date"
            value={getDateObject(selectedDate)?.toISOString()?.split('T')?.[0]}
            onChange={(e) => {
              onDateChange(new Date(e.target.value));
              setShowCalendar(false);
            }}
            max={new Date()?.toISOString()?.split('T')?.[0]}
            className="w-full px-3 py-2 border border-border rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )}
      {/* Quick Navigation */}
      <div className="flex flex-wrap gap-2 mt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDateChange(new Date())}
          disabled={isToday(selectedDate)}
        >
          Today
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const yesterday = new Date();
            yesterday?.setDate(yesterday?.getDate() - 1);
            onDateChange(yesterday);
          }}
        >
          Yesterday
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const weekAgo = new Date();
            weekAgo?.setDate(weekAgo?.getDate() - 7);
            onDateChange(weekAgo);
          }}
        >
          Week Ago
        </Button>
      </div>
    </div>
  );
};

export default DateNavigation;